import { useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
    Button,
    Col,
    Modal,
    ProTableOt,
    Row,
    Tag,
    useCRUDController,
    useProTableOt,
} from '../../ui/ot';

const getSnapshot = (record) => record?.after_data || record?.before_data || {};

const getListNumber = (record) => {
    const list = getSnapshot(record)?.list || {};
    if (!list.list_number_text) return record?.list_id || '-';
    if (list.list_number_serial === null || list.list_number_serial === undefined) {
        return list.list_number_text;
    }
    return `${list.list_number_text}${String(list.list_number_serial).padStart(3, '0')}`;
};

const getListName = (record) => {
    const listTasks = getSnapshot(record)?.list_tasks || [];
    return listTasks[0]?.task_name || '-';
};

const normalizeForCompare = (value) => {
    if (Array.isArray(value)) return value.map(normalizeForCompare);
    if (!value || typeof value !== 'object') return value;

    return Object.entries(value).reduce((result, [key, item]) => {
        if (key === 'last_edit_user_id' || key === 'last_edit_time') return result;
        result[key] = normalizeForCompare(item);
        return result;
    }, {});
};

const getChangedSections = (record) => {
    if (record?.operation === 'DELETE') return ['刪除清單'];

    const before = normalizeForCompare(record?.before_data || {});
    const after = normalizeForCompare(record?.after_data || {});
    const sections = [
        ['list', '清單資料'],
        ['list_tasks', '清單名稱'],
        ['bibs_list_items', '書目'],
        ['subscription_list_items', '期刊'],
        ['items_list_items', '館藏'],
    ];

    return sections
        .filter(([key]) => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
        .map(([, label]) => label);
};

const Snapshot = ({ title, data }) => (
    <Col span={12}>
        <h4>{title}</h4>
        <pre style={{
            background: '#f5f5f5',
            borderRadius: 4,
            margin: 0,
            maxHeight: 520,
            overflow: 'auto',
            padding: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
        }}>
            {JSON.stringify(data ?? null, null, 2)}
        </pre>
    </Col>
);

const ScrappedListChangeLogTable = () => {
    const actionRef = useRef();
    const [selectedRecord, setSelectedRecord] = useState(null);
    const controller = useCRUDController({
        apiRoute: '/library/scrapped_list/change_log',
    });
    const columns = [
        {
            title: '清單編號',
            key: 'list_number',
            render: (_, record) => getListNumber(record),
        },
        {
            title: '清單名稱',
            key: 'list_name',
            render: (_, record) => getListName(record),
        },
        {
            title: '更正內容',
            key: 'changed_sections',
            render: (_, record) => (
                <Row gutter={[4, 4]}>
                    {getChangedSections(record).map((section) => (
                        <Col key={section}><Tag>{section}</Tag></Col>
                    ))}
                </Row>
            ),
        },
        {
            title: '操作類型',
            dataIndex: 'operation',
            key: 'operation',
            render: (operation) => (
                <Tag color={operation === 'DELETE' ? 'error' : 'processing'}>
                    {operation === 'DELETE' ? '刪除' : '編輯'}
                </Tag>
            ),
        },
        {
            title: '申請人員',
            dataIndex: 'changed_user_name',
            key: 'changed_user_name',
            render: (text, record) => text || record?.changed_user_id || '-',
        },
        {
            title: '申請時間',
            dataIndex: 'changed_at',
            key: 'changed_at',
            render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Button onClick={() => setSelectedRecord(record)}>查看明細</Button>
            ),
        },
    ];
    const { check, setCheck } = useProTableOt({ columns });

    return <>
        <ProTableOt
            actionRef={actionRef}
            columns={columns}
            check={check}
            setCheck={setCheck}
            rowKey="scrapped_list_change_log_id"
            rowSelection={false}
            search={false}
            request={async (params = {}) => controller.getData({
                operations: 'UPDATE,DELETE',
                size: params.pageSize,
                cur_page: params.current,
            })}
        />
        <Modal
            open={selectedRecord !== null}
            onClose={() => setSelectedRecord(null)}
            closeText="關閉"
            close={true}
            title="更正紀錄明細"
            width={1400}
        >
            <Row gutter={[16, 16]}>
                <Snapshot title="更正前" data={selectedRecord?.before_data} />
                <Snapshot title="更正後" data={selectedRecord?.after_data} />
            </Row>
        </Modal>
    </>;
};

const ScrappedListChangeLogModal = () => {
    const [open, setOpen] = useState(false);

    return <>
        <Button onClick={() => setOpen(true)}>查看更正紀錄</Button>
        <Modal
            open={open}
            onClose={() => setOpen(false)}
            closeText="關閉"
            close={true}
            title="更正紀錄"
            width={1600}
        >
            <ScrappedListChangeLogTable />
        </Modal>
    </>;
};

export default ScrappedListChangeLogModal;
